/**
 * BulkImport — drop an entire policy manual into the Policy Library at once.
 *
 * One drop zone accepts multiple files AND whole folders (walked recursively
 * via webkitGetAsEntry), plus a multi-select picker. Each document goes
 * through the existing single-file AI parse (metadata extracted from THAT
 * document: title, policy number, its own dates, citations, verbatim content),
 * then lands in a review grid where every value is editable.
 *
 * The date guard: a row cannot import until it has a policy number, a title,
 * and an effective date. Dates are never defaulted from the batch — they come
 * from each document or from the reviewer's own hands, visibly.
 *
 * Import runs through the existing /upload/confirm endpoint per row with a
 * shared import_batch_id so the whole batch is one traceable operation in the
 * audit trail.
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ArrowUpTrayIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const API = process.env.REACT_APP_API_URL;

const isPolicyFile = (name) => /\.(pdf|docx?)$/i.test(name || '');

// Recursively collect File objects from a drop event that may contain
// directories. Falls back to plain files when the entry API is unavailable.
async function collectDroppedFiles(dataTransfer) {
  const out = [];
  const items = Array.from(dataTransfer.items || []);
  const walkers = [];

  const walkEntry = (entry) => new Promise((resolve) => {
    if (!entry) return resolve();
    if (entry.isFile) {
      entry.file((f) => { if (isPolicyFile(f.name)) out.push(f); resolve(); }, () => resolve());
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      const readBatch = () => {
        reader.readEntries(async (entries) => {
          if (!entries.length) return resolve();
          await Promise.all(entries.map(walkEntry));
          readBatch(); // readEntries returns results in chunks; keep reading
        }, () => resolve());
      };
      readBatch();
    } else resolve();
  });

  for (const item of items) {
    const entry = item.webkitGetAsEntry && item.webkitGetAsEntry();
    if (entry) walkers.push(walkEntry(entry));
  }
  if (walkers.length) {
    await Promise.all(walkers);
    return out;
  }
  return Array.from(dataTransfer.files || []).filter(f => isPolicyFile(f.name));
}

const STATUS_CFG = {
  queued:       { label: 'Queued',        color: 'bg-slate-50 text-slate-500 border-slate-200' },
  parsing:      { label: 'Reading…',      color: 'bg-orange-50 text-orange-700 border-orange-200' },
  ready:        { label: 'Ready',         color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  needs_input:  { label: 'Needs your input', color: 'bg-amber-50 text-amber-700 border-amber-300' },
  error:        { label: 'Failed to read', color: 'bg-red-50 text-red-700 border-red-200' },
  importing:    { label: 'Importing…',    color: 'bg-orange-50 text-orange-700 border-orange-200' },
  imported:     { label: 'Imported',      color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  import_error: { label: 'Import failed', color: 'bg-red-50 text-red-700 border-red-200' },
};

const CATEGORY_OPTIONS = ['compliance', 'operational', 'clinical', 'privacy', 'financial', 'it', 'vendor', 'other'];

function rowReadiness(row) {
  if (!['ready', 'needs_input'].includes(row.status)) return row.status;
  const hasAll = (row.policy_number || '').trim() && (row.title || '').trim() && (row.effective_date || '').trim();
  return hasAll ? 'ready' : 'needs_input';
}

export default function BulkImport({ open, onClose, onImported }) {
  const [rows, setRows] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [parsingCount, setParsingCount] = useState(0);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const nextId = useRef(1);

  // Org user list for the Owner column: the document names a person; the
  // server matches that name to an account and we PRE-SELECT it here for the
  // human to confirm or change — never a silent assignment.
  const [orgUsers, setOrgUsers] = useState([]);
  // ALL registered departments for the Department column — pre-selected from
  // the server's department-name resolution, changeable per row.
  const [orgDepartments, setOrgDepartments] = useState([]);
  useEffect(() => {
    if (!open) return;
    fetch(`${API}/api/calendar/assignable-users`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { const list = Array.isArray(d) ? d : (d?.users || []); setOrgUsers(list); })
      .catch(() => {});
    fetch(`${API}/api/departments`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { const list = Array.isArray(d) ? d : (d?.departments || []); setOrgDepartments(list); })
      .catch(() => {});
  }, [open]);

  const updateRow = (id, patch) => setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));

  const parseOne = useCallback(async (row) => {
    updateRow(row.id, { status: 'parsing' });
    const form = new FormData();
    form.append('file', row.file);
    // Two attempts: the import limiter allows 40/min, but if a huge batch
    // trips it we wait and retry once instead of failing the row.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const res = await fetch(`${API}/api/ppm/policies/upload`, { method: 'POST', credentials: 'include', body: form });
        if (res.status === 429 && attempt === 0) {
          await new Promise(r => setTimeout(r, 20000));
          continue;
        }
        const data = await res.json();
        if (!data.success) { updateRow(row.id, { status: 'error', error: data.message || 'Could not read this file' }); return; }
        const p = data.parsed || {};
        updateRow(row.id, {
          status: 'ready',
          upload_id: data.upload_id,
          policy_number: p.policy_number || '',
          title: p.title || '',
          description: p.description || '',
          category: p.category || 'compliance',
          effective_date: p.effective_date || '',
          approval_date: p.approval_date || '',
          expiration_date: p.expiration_date || '',
          approved_by_name: p.approved_by || '',
          revision_number: p.revision_number || '',
          key_sections: p.key_sections || null,
          content: p.content || '',
          cfr_citations: p.cfr_citations || [],
          department_id: data.resolved_department_id || '',
          dept_from_doc: !!data.resolved_department_id,
          // Owner: pre-select the server's name match (exact/strong only);
          // weak matches are shown as a hint, not selected.
          owner_user_id: (data.suggested_owner && ['exact', 'strong'].includes(data.suggested_owner.quality)) ? data.suggested_owner.user_id : '',
          suggested_owner: data.suggested_owner || null,
          confidence: p.confidence || 'low',
          duplicate_warning: data.duplicate_warning || null,
          // provenance: remember which values the AI read from the document
          ai_dates: { effective: !!p.effective_date, approval: !!p.approval_date },
        });
        return;
      } catch (e) {
        if (attempt === 1) updateRow(row.id, { status: 'error', error: 'Upload failed' });
      }
    }
  }, []);

  const enqueue = useCallback(async (files) => {
    const fresh = files.map(f => ({ id: nextId.current++, file: f, name: f.name, status: 'queued' }));
    if (!fresh.length) return;
    setRows(prev => [...prev, ...fresh]);
    // Concurrency 2: parses are model calls (~5-10s each); two in flight keeps
    // a 50-file batch under a few minutes without hammering the API.
    setParsingCount(c => c + fresh.length);
    const queue = [...fresh];
    const worker = async () => {
      while (queue.length) {
        const row = queue.shift();
        await parseOne(row);
        setParsingCount(c => c - 1);
      }
    };
    await Promise.all([worker(), worker()]);
  }, [parseOne]);

  const handleDrop = async (e) => {
    e.preventDefault(); setDragOver(false);
    const files = await collectDroppedFiles(e.dataTransfer);
    enqueue(files);
  };

  const importAll = async () => {
    const importable = rows.filter(r => rowReadiness(r) === 'ready');
    if (!importable.length || importing) return;
    setImporting(true);
    const batchId = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `batch-${Date.now()}`;
    for (const row of importable) {
      updateRow(row.id, { status: 'importing' });
      try {
        const res = await fetch(`${API}/api/ppm/policies/upload/confirm`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            upload_id: row.upload_id,
            import_batch_id: batchId,
            policy_number: row.policy_number.trim(),
            title: row.title.trim(),
            description: row.description || '',
            category: row.category || 'compliance',
            department_id: row.department_id || null,
            owner_user_id: row.owner_user_id || null,
            review_cycle_months: 12,
            effective_date: row.effective_date,
            approval_date: row.approval_date || null,
            expiration_date: row.expiration_date || null,
            approved_by_name: row.approved_by_name || null,
            revision_number: row.revision_number || null,
            key_sections: row.key_sections,
            content: row.content,
            change_summary: 'Bulk import of existing policy',
            cfr_citations: row.cfr_citations,
            file_name: row.name,
          }),
        });
        const data = await res.json();
        if (data.success) updateRow(row.id, { status: 'imported', policy_id: data.policy?.id });
        else updateRow(row.id, { status: 'import_error', error: data.message || 'Import failed' });
      } catch {
        updateRow(row.id, { status: 'import_error', error: 'Request failed' });
      }
    }
    setImporting(false);
    if (onImported) onImported();
  };

  if (!open) return null;

  const readyCount = rows.filter(r => rowReadiness(r) === 'ready').length;
  const needsInput = rows.filter(r => rowReadiness(r) === 'needs_input').length;
  const importedCount = rows.filter(r => r.status === 'imported').length;
  const errorCount = rows.filter(r => r.status === 'error' || r.status === 'import_error').length;
  const busy = parsingCount > 0 || importing;

  const inputCls = 'w-full rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-700 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-400 outline-none';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40" onClick={() => !busy && onClose()} />
      <div className="relative bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-bold text-slate-800">Bulk import policies</h3>
            <p className="text-xs text-slate-400 mt-0.5 max-w-2xl">
              Drop your whole policy manual, files or folders. Each document is read individually: its own title, its own policy number, its own dates. Nothing imports until it has an effective date, and no date is ever filled in for you from the batch.
            </p>
          </div>
          <button onClick={() => !busy && onClose()} disabled={busy}
            className="text-slate-400 hover:text-slate-600 text-sm font-medium disabled:opacity-40">Close</button>
        </div>

        {/* Drop zone */}
        <div className="px-6 pt-4">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`rounded-2xl border-2 border-dashed px-6 py-6 text-center cursor-pointer transition-colors ${
              dragOver ? 'border-orange-400 bg-orange-50/60' : 'border-slate-200 hover:border-orange-300 hover:bg-orange-50/30'
            }`}
          >
            <ArrowUpTrayIcon className="w-6 h-6 mx-auto text-orange-500 mb-1.5" strokeWidth={1.75} />
            <p className="text-sm font-semibold text-slate-700">Drag files or entire folders here</p>
            <p className="text-xs text-slate-400 mt-0.5">PDF, DOCX, DOC · or click to browse (multi-select)</p>
            <input
              ref={fileInputRef} type="file" multiple accept=".pdf,.docx,.doc" className="hidden"
              onChange={(e) => { enqueue(Array.from(e.target.files || []).filter(f => isPolicyFile(f.name))); e.target.value = ''; }}
            />
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {rows.length === 0 && (
            <p className="text-xs text-slate-400 italic text-center py-6">Nothing queued yet.</p>
          )}
          {rows.length > 0 && (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2 w-32">Document</th>
                  <th className="px-3 py-2 w-28">Policy ID</th>
                  <th className="px-3 py-2 min-w-[260px]">Title</th>
                  <th className="px-3 py-2 w-32">Effective date</th>
                  <th className="px-3 py-2 w-32">Approval date</th>
                  {/* Category column HIDDEN (not removed) per operator decision
                      2026-07-22: departments are what the business files by; the
                      AI-extracted category still saves silently for library
                      filters. Restore the header + cell below to bring it back. */}
                  <th className="px-3 py-2 w-44">Department</th>
                  <th className="px-3 py-2 w-40">Owner</th>
                  <th className="px-3 py-2 w-32">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(row => {
                  const eff = rowReadiness(row);
                  const cfg = STATUS_CFG[eff] || STATUS_CFG.queued;
                  const editable = ['ready', 'needs_input'].includes(eff) && !importing;
                  return (
                    <tr key={row.id} className={eff === 'needs_input' ? 'bg-amber-50/40' : ''}>
                      <td className="px-3 py-2 align-top">
                        <div className="text-xs text-slate-600 font-medium break-all max-w-[180px]">{row.name}</div>
                        {row.duplicate_warning && (
                          <div className="flex items-start gap-1 mt-1 text-[10px] text-amber-700">
                            <ExclamationTriangleIcon className="w-3 h-3 flex-shrink-0 mt-px" />{row.duplicate_warning}
                          </div>
                        )}
                        {row.error && <div className="text-[10px] text-red-600 mt-1">{row.error}</div>}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {editable ? <input value={row.policy_number || ''} onChange={e => updateRow(row.id, { policy_number: e.target.value })} placeholder="Required" className={inputCls} />
                          : <span className="text-xs font-mono text-slate-600">{row.policy_number || '—'}</span>}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {editable ? (
                          <textarea value={row.title || ''} onChange={e => updateRow(row.id, { title: e.target.value })}
                            placeholder="Required — the policy's title as written on the document"
                            rows={Math.min(4, Math.max(2, Math.ceil((row.title || '').length / 45)))}
                            className={`${inputCls} resize-y leading-snug min-w-[250px]`} />
                        ) : <span className="text-xs text-slate-700">{row.title || '—'}</span>}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {editable ? (
                          <div>
                            <input type="date" value={row.effective_date || ''} onChange={e => updateRow(row.id, { effective_date: e.target.value, ai_dates: { ...row.ai_dates, effective: false } })} className={inputCls} />
                            <div className={`text-[9px] mt-0.5 ${row.ai_dates?.effective ? 'text-emerald-600' : 'text-slate-400'}`}>
                              {row.effective_date ? (row.ai_dates?.effective ? 'read from document' : 'entered by you') : 'required — from this document, not the batch'}
                            </div>
                          </div>
                        ) : <span className="text-xs text-slate-600">{row.effective_date || '—'}</span>}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {editable ? <input type="date" value={row.approval_date || ''} onChange={e => updateRow(row.id, { approval_date: e.target.value, ai_dates: { ...row.ai_dates, approval: false } })} className={inputCls} />
                          : <span className="text-xs text-slate-600">{row.approval_date || '—'}</span>}
                      </td>
                      {false && (
                      <td className="px-3 py-2 align-top">
                        {editable ? (
                          <select value={row.category || 'compliance'} onChange={e => updateRow(row.id, { category: e.target.value })} className={inputCls}>
                            {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        ) : <span className="text-xs text-slate-600 capitalize">{row.category || '—'}</span>}
                      </td>
                      )}
                      <td className="px-3 py-2 align-top">
                        {editable ? (
                          <div>
                            <select value={row.department_id || ''} onChange={e => updateRow(row.id, { department_id: e.target.value, dept_from_doc: false })} className={inputCls}>
                              <option value="">No department</option>
                              {orgDepartments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                            <div className={`text-[9px] mt-0.5 ${row.dept_from_doc ? 'text-emerald-600' : 'text-slate-400'}`}>
                              {row.dept_from_doc ? 'read from document' : row.department_id ? 'chosen by you' : 'pick if the document names one'}
                            </div>
                          </div>
                        ) : <span className="text-xs text-slate-600">{(orgDepartments.find(d => d.id === row.department_id)?.name) || '—'}</span>}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {editable ? (
                          <div>
                            <select value={row.owner_user_id || ''} onChange={e => updateRow(row.id, { owner_user_id: e.target.value })} className={inputCls}>
                              <option value="">You (uploader)</option>
                              {orgUsers.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                            </select>
                            <div className={`text-[9px] mt-0.5 ${
                              row.suggested_owner && row.owner_user_id === row.suggested_owner.user_id ? 'text-emerald-600'
                                : row.suggested_owner ? 'text-amber-600' : 'text-slate-400'
                            }`}>
                              {row.suggested_owner
                                ? (row.owner_user_id === row.suggested_owner.user_id
                                  ? `matched "${row.suggested_owner.matched_from}" from the document`
                                  : `document names "${row.suggested_owner.matched_from}" — possible: ${row.suggested_owner.name}`)
                                : 'no owner named in the document'}
                            </div>
                          </div>
                        ) : <span className="text-xs text-slate-600">{(orgUsers.find(u => u.id === row.owner_user_id)?.name) || '—'}</span>}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cfg.color}`}>
                          {eff === 'imported' && <CheckCircleIcon className="w-3 h-3" />}
                          {cfg.label}
                        </span>
                        {row.confidence && ['ready', 'needs_input'].includes(eff) && (
                          <div className="text-[9px] text-slate-400 mt-0.5">extraction: {row.confidence}</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-4 flex-wrap">
          <div className="text-xs text-slate-500">
            {rows.length > 0 && (
              <>
                {readyCount} ready{needsInput > 0 && <span className="text-amber-700"> · {needsInput} need a date, title, or policy ID</span>}
                {importedCount > 0 && <span className="text-emerald-700"> · {importedCount} imported</span>}
                {errorCount > 0 && <span className="text-red-600"> · {errorCount} failed</span>}
                {parsingCount > 0 && <span className="text-orange-600"> · reading {parsingCount}…</span>}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => !busy && onClose()} disabled={busy}
              className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 disabled:opacity-40 transition-colors">
              {importedCount > 0 ? 'Done' : 'Cancel'}
            </button>
            <button onClick={importAll} disabled={readyCount === 0 || busy}
              className="px-5 py-2 rounded-xl bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              {importing ? 'Importing…' : `Import ${readyCount} ${readyCount === 1 ? 'policy' : 'policies'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
